/**
 * Create Custom Role Modal
 *
 * Modal for creating custom (non-system) roles in the 5-tier hierarchy.
 * Supports parent role selection and initial permission assignment.
 */

import { useState, useEffect } from 'react';
import { Card, Button, Input, Select } from '@aaimplatform/ui';
import { createRbacClient } from '@aaimplatform/institutional-types/api-clients';
import type { components } from '@aaimplatform/institutional-types/rbac-management';
import { authMiddleware } from '../api/client';
import { formatApiError } from '../lib/api-error';

type Role = components['schemas']['Role'];
type Permission = components['schemas']['Permission'];
type CreateRoleRequest = components['schemas']['CreateRoleRequest'];

interface CreateRoleModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (role: Role) => void;
}

interface FormData {
  name: string;
  displayName: string;
  tier: number;
  providerFamily: string;
  parentRoleId: string;
  selectedPermissions: string[];
}

const INITIAL_FORM_DATA: FormData = {
  name: '',
  displayName: '',
  tier: 2,
  providerFamily: '',
  parentRoleId: '',
  selectedPermissions: [],
};

const TIER_LABELS = [
  { value: 0, label: 'Tier 0: APEX (Platform-wide)' },
  { value: 1, label: 'Tier 1: Core Processor (Multi-tenant)' },
  { value: 2, label: 'Tier 2: Tenant (Single FI)' },
  { value: 3, label: 'Tier 3: Consumer (Individual)' },
  { value: 4, label: 'Tier 4: Delegate (Delegated access)' },
];

export function CreateRoleModal({ open, onClose, onSuccess }: CreateRoleModalProps) {
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [availablePermissions, setAvailablePermissions] = useState<Permission[]>([]);

  const client = createRbacClient();
  // CSRF double-submit (N8) echo + auth-broken bounce on this mutating client.
  client.use(authMiddleware());

  useEffect(() => {
    if (open) {
      loadAvailableData();
      setFormData(INITIAL_FORM_DATA);
      setErrors({});
      setError(null);
    }
  }, [open]);

  async function loadAvailableData() {
    try {
      // Load roles for parent selection
      const { data: rolesData } = await client.GET('/api/v1/roles', {});
      if (rolesData && 'success' in rolesData && rolesData.success && 'data' in rolesData) {
        setAvailableRoles(rolesData.data);
      }

      // Load permissions for initial assignment
      const { data: permsData } = await client.GET('/api/v1/permissions', {});
      if (permsData && 'success' in permsData && permsData.success && 'data' in permsData) {
        setAvailablePermissions(permsData.data);
      }
    } catch (err) {
      // Silent fail - optional data
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate name (UPPER_SNAKE_CASE)
    if (!formData.name.trim()) {
      newErrors.name = 'Role name is required';
    } else if (!/^[A-Z][A-Z0-9_]*$/.test(formData.name)) {
      newErrors.name = 'Name must be UPPER_SNAKE_CASE (e.g., CUSTOM_ADMIN_ROLE)';
    }

    if (!formData.displayName.trim()) {
      newErrors.displayName = 'Display name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const requestBody: CreateRoleRequest = {
        name: formData.name.trim(),
        displayName: formData.displayName.trim(),
        tier: formData.tier,
        ...(formData.providerFamily && { providerFamily: formData.providerFamily.trim() }),
        ...(formData.parentRoleId && { parentRoleId: formData.parentRoleId }),
        ...(formData.selectedPermissions.length > 0 && { permissionIds: formData.selectedPermissions }),
      };

      const { data, error: apiError, response } = await client.POST('/api/v1/roles', {
        body: requestBody,
      });

      if (apiError) {
        setError(formatApiError(apiError, 'Failed to create role', response?.status));
        return;
      }

      if (data && 'success' in data && data.success && 'data' in data) {
        onSuccess(data.data);
        onClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  // Filter parent roles: same tier or higher only
  const eligibleParentRoles = availableRoles.filter((role) => role.tier <= formData.tier);

  // Group permissions by category for display
  const permissionsByCategory = availablePermissions.reduce((acc, perm) => {
    const category = perm.category || 'other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(perm);
    return acc;
  }, {} as Record<string, Permission[]>);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-xl font-sans font-semibold text-primary mb-6">Create Custom Role</h2>

          {error && (
            <div className="mb-4 p-3 bg-error/10 border border-error/30 rounded-sm">
              <p className="text-sm text-error">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            {/* Role Name */}
            <div>
              <label className="block text-sm font-sans font-medium text-primary mb-1">
                Role Name <span className="text-error">*</span>
              </label>
              <Input
                value={formData.name}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    name: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_'),
                  })
                }
                placeholder="CUSTOM_ADMIN_ROLE"
                className="w-full"
              />
              {errors.name ? (
                <span className="text-xs text-error mt-1 block">{errors.name}</span>
              ) : (
                <span className="text-xs text-tertiary mt-1 block">
                  Uppercase letters, numbers, and underscores only
                </span>
              )}
            </div>

            {/* Display Name */}
            <div>
              <label className="block text-sm font-sans font-medium text-primary mb-1">
                Display Name <span className="text-error">*</span>
              </label>
              <Input
                value={formData.displayName}
                onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                placeholder="Custom Admin Role"
                className="w-full"
              />
              {errors.displayName && (
                <span className="text-xs text-error mt-1 block">{errors.displayName}</span>
              )}
            </div>

            {/* Tier */}
            <div>
              <label className="block text-sm font-sans font-medium text-primary mb-1">
                Tier <span className="text-error">*</span>
              </label>
              <Select
                value={String(formData.tier)}
                onChange={(e) => setFormData({ ...formData, tier: parseInt(e.target.value) })}
                className="w-full"
              >
                {TIER_LABELS.map((tier) => (
                  <option key={tier.value} value={tier.value}>
                    {tier.label}
                  </option>
                ))}
              </Select>
              <span className="text-xs text-tertiary mt-1 block">Custom roles typically use Tier 2 or 3</span>
            </div>

            {/* Provider Family (optional) */}
            {formData.tier <= 2 && (
              <div>
                <label className="block text-sm font-sans font-medium text-primary mb-1">
                  Provider Family (Optional)
                </label>
                <Input
                  value={formData.providerFamily}
                  onChange={(e) => setFormData({ ...formData, providerFamily: e.target.value })}
                  placeholder="cuanswers, jack_henry, fiserv, etc."
                  className="w-full"
                />
                <span className="text-xs text-tertiary mt-1 block">
                  Leave blank for platform-wide roles. Specify for core processor-specific roles (Tier 1-2).
                </span>
              </div>
            )}

            {/* Parent Role (optional) */}
            {eligibleParentRoles.length > 0 && (
              <div>
                <label className="block text-sm font-sans font-medium text-primary mb-1">
                  Parent Role (Optional)
                </label>
                <Select
                  value={formData.parentRoleId}
                  onChange={(e) => setFormData({ ...formData, parentRoleId: e.target.value })}
                  className="w-full"
                >
                  <option value="">No parent (root role)</option>
                  {eligibleParentRoles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name} (Tier {role.tier})
                    </option>
                  ))}
                </Select>
                <span className="text-xs text-tertiary mt-1 block">
                  Parent must be same tier or higher. Role inherits parent permissions.
                </span>
              </div>
            )}

            {/* Initial Permissions (optional) */}
            <div>
              <label className="block text-sm font-sans font-medium text-primary mb-2">
                Initial Permissions (Optional)
              </label>
              <div className="border border-recessed rounded-sm p-3 max-h-64 overflow-y-auto">
                {Object.keys(permissionsByCategory).length === 0 ? (
                  <div className="text-sm text-tertiary">Loading permissions...</div>
                ) : (
                  Object.entries(permissionsByCategory).map(([category, perms]) => (
                    <div key={category} className="mb-3 last:mb-0">
                      <div className="text-xs font-sans font-semibold text-tertiary mb-1">
                        {category.toUpperCase()}
                      </div>
                      <div className="space-y-1">
                        {perms.slice(0, 5).map((perm) => (
                          <label key={perm.id} className="flex items-start gap-2 text-sm hover:bg-surface p-1 rounded">
                            <input
                              type="checkbox"
                              checked={formData.selectedPermissions.includes(perm.id)}
                              onChange={(e) => {
                                const newSelected = e.target.checked
                                  ? [...formData.selectedPermissions, perm.id]
                                  : formData.selectedPermissions.filter((id) => id !== perm.id);
                                setFormData({ ...formData, selectedPermissions: newSelected });
                              }}
                              className="mt-0.5"
                            />
                            <span className="font-mono text-xs">{perm.permissionString}</span>
                          </label>
                        ))}
                        {perms.length > 5 && (
                          <div className="text-xs text-tertiary pl-6">
                            +{perms.length - 5} more (assign after creation)
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
              <span className="text-xs text-tertiary mt-1 block">
                You can assign more permissions after creating the role
              </span>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <Button variant="ghost" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={loading}>
              {loading ? 'Creating...' : 'Create Role'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
